import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useCustomerChargeTerms, useSaveCustomerChargeTerms } from '../../services/chargeControls';
import { DEFAULT_CHARGE_FREQUENCY_LABELS, describeChargeFrequency } from '../loan-application/steps/DefaultTermsFields';
import { formatSouthAfricanDateTime } from '../../utils/southAfricanTime';

/**
 * A customer's personal default-charge terms (#38, presentation 23 Oct 2026). Saving applies them to the
 * customer's open loans, except a loan whose terms were set on the loan itself, and they prefill the
 * default terms of the customer's new applications.
 */
const CustomerChargeTermsCard: React.FC<{ customerPartyId: string }> = ({ customerPartyId }) => {
  const { data: terms, isLoading } = useCustomerChargeTerms(customerPartyId);
  const save = useSaveCustomerChargeTerms(customerPartyId);
  const [editing, setEditing] = useState(false);
  const [rate, setRate] = useState('');
  const [frequency, setFrequency] = useState('');
  const [intervalDays, setIntervalDays] = useState('');
  const [graceDays, setGraceDays] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    setRate(terms?.defaultInterestRate != null ? String(terms.defaultInterestRate) : '');
    setFrequency(terms?.defaultChargeFrequency || '');
    setIntervalDays(terms?.defaultChargeIntervalDays != null ? String(terms.defaultChargeIntervalDays) : '');
    setGraceDays(terms?.defaultGraceDays != null ? String(terms.defaultGraceDays) : '');
    setReason(terms?.reason || '');
  }, [terms, editing]);

  const submit = async () => {
    if (!reason.trim()) { toast.error('Give a reason for these personal charge terms'); return; }
    if (frequency === 'CUSTOM_DAYS' && !(Number(intervalDays) >= 1)) { toast.error('Enter how many days apart the charge is made'); return; }
    try {
      const saved = await save.mutateAsync({
        defaultInterestRate: rate === '' ? undefined : Number(rate),
        defaultChargeFrequency: frequency || undefined,
        defaultChargeIntervalDays: frequency === 'CUSTOM_DAYS' ? Number(intervalDays) : undefined,
        defaultGraceDays: graceDays === '' ? undefined : Number(graceDays),
        reason: reason.trim(),
      });
      toast.success(`Saved. Applied to ${saved.loansUpdated ?? 0} open loan(s)`
        + (saved.loansWithOwnTerms ? `; ${saved.loansWithOwnTerms} kept their own terms` : '') + '.');
      setEditing(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'The charge terms could not be saved');
    }
  };

  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <h6 className="mb-0"><i className="fa fa-percent me-2 text-primary"></i>Personal charge terms</h6>
        {!editing && (
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setEditing(true)} disabled={isLoading}>
            {terms ? 'Change' : 'Set personal terms'}
          </button>
        )}
      </div>
      <div className="card-body">
        {!editing ? (
          terms ? (
            <>
              <p className="mb-1">
                <strong>{terms.defaultInterestRate != null ? `${terms.defaultInterestRate}%` : 'Loan rate'}</strong>
                {' '}charged {describeChargeFrequency(terms.defaultChargeFrequency || undefined, terms.defaultChargeIntervalDays || undefined)}
                {terms.defaultGraceDays ? `, after ${terms.defaultGraceDays} grace day${terms.defaultGraceDays === 1 ? '' : 's'}` : ', no grace period'}.
              </p>
              <p className="small text-muted mb-0">
                {terms.reason}{terms.modifiedBy ? ` · set by ${terms.modifiedBy}` : ''}{terms.modifiedDate ? ` · ${formatSouthAfricanDateTime(terms.modifiedDate)}` : ''}
              </p>
            </>
          ) : (
            <p className="text-muted small mb-0">
              None: each loan uses the default terms agreed on its application. Personal terms apply to all of this
              customer's open loans (except a loan with its own terms) and prefill their new applications.
            </p>
          )
        ) : (
          <div className="row g-2">
            <div className="col-md-3">
              <label className="form-label small" htmlFor="cct-rate">Rate (%)</label>
              <input id="cct-rate" type="number" min={0} step="0.01" className="form-control form-control-sm" value={rate} onChange={e => setRate(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label small" htmlFor="cct-frequency">How often</label>
              <select id="cct-frequency" className="form-select form-select-sm" value={frequency} onChange={e => setFrequency(e.target.value)}>
                <option value="">Monthly (standard)</option>
                {Object.entries(DEFAULT_CHARGE_FREQUENCY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            {frequency === 'CUSTOM_DAYS' && (
              <div className="col-md-2">
                <label className="form-label small" htmlFor="cct-interval">Every (days)</label>
                <input id="cct-interval" type="number" min={1} className="form-control form-control-sm" value={intervalDays} onChange={e => setIntervalDays(e.target.value)} />
              </div>
            )}
            <div className="col-md-2">
              <label className="form-label small" htmlFor="cct-grace">Grace days</label>
              <input id="cct-grace" type="number" min={0} className="form-control form-control-sm" value={graceDays} onChange={e => setGraceDays(e.target.value)} />
            </div>
            <div className="col-12">
              <label className="form-label small" htmlFor="cct-reason">Reason <span className="text-danger">*</span></label>
              <input id="cct-reason" type="text" maxLength={500} className="form-control form-control-sm" value={reason}
                placeholder="e.g. Long-standing client, agreed by the credit manager" onChange={e => setReason(e.target.value)} />
            </div>
            <div className="col-12 d-flex justify-content-end gap-2 mt-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(false)} disabled={save.isPending}>Cancel</button>
              <button type="button" className="btn btn-sm btn-primary" onClick={submit} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save and apply to open loans'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerChargeTermsCard;
